/**
 * Node valence — does the workshop want this consequence to happen?
 *
 * Distinct from effect-type polarity, and the distinction is the whole point.
 * Polarity says which way an edge pushes: `vahvistaa` is `+` whether it
 * strengthens something good or something catastrophic. Valence says whether
 * the room wants the thing at the end of that edge at all. Three "vahvistaa"
 * links can accelerate a disaster (specs/impact-path-v2-prd.md §5), and until
 * now nothing in the map could say so.
 *
 * Three states, because a futures map is mostly the third one. Collapsing
 * "we don't know yet" into neutral would erase the reason the workshop is
 * mapping at all — an unknown consequence is a question worth carrying, not an
 * absence of opinion.
 *
 * Copies: packages/shared/valence.js (the display imports it),
 * apps/backend/src/shared/ and apps/rag-service/src/shared/ (Railway builds
 * each service from its own folder, so they cannot import packages/).
 * scripts/check-shared-copies.js keeps them identical.
 *
 * NULL in the column is a fourth thing and not a state: nobody and nothing has
 * judged this node yet. That is what lets the AI fill gaps without ever
 * overwriting a person — see `valence_source`.
 */

export const VALENCE = {
  wanted: { polarity: '+', label: 'Toivottu' },
  unwanted: { polarity: '-', label: 'Ei-toivottu' },
  unknown: { polarity: '0', label: 'Epäselvä' },
};

export const VALENCE_KEYS = Object.keys(VALENCE);

/** Who decided. A human judgement is never replaced by a later AI pass. */
export const VALENCE_SOURCES = ['human', 'ai'];

/** @returns {string|null} canonical valence, or null if unrecognised */
export function normalizeValence(value) {
  if (typeof value !== 'string') return null;
  const key = value.trim().toLowerCase();
  return VALENCE[key] ? key : null;
}

/**
 * Propagate a parent's valence across an edge.
 *
 * The arithmetic loops already use, one level up: a negative edge flips what
 * follows. Weakening something unwanted is wanted; reinforcing it is not. Used
 * to PROPOSE a valence for a node nobody has judged — never to set one.
 *
 * @param {string|null} parentValence
 * @param {string} edgePolarity  '+' | '-' | 'enabling' | '0'
 * @returns {string|null} proposed valence, or null when nothing follows
 */
export function propagateValence(parentValence, edgePolarity) {
  if (!parentValence || parentValence === 'unknown') return null;
  if (edgePolarity === '0') return null;          // lateral: carries nothing
  if (edgePolarity === '-') {
    return parentValence === 'wanted' ? 'unwanted' : 'wanted';
  }
  return parentValence;                            // '+' and 'enabling' carry through
}

/**
 * Choice points — where a node's consequences fork into wanted and unwanted.
 *
 * Nobody authors these. A node whose children include both something the room
 * wants and something it does not is, structurally, a place where the system
 * can still go either way — and that is the definition of a decision worth
 * making. It is the same move as loop classification: the arithmetic finds the
 * shape, a person decides what it means.
 *
 * This is the structural, explainable candidate list for vipupisteet that
 * specs/systems-analysis-visualisation.md §2 asks for. A pivot point discovered
 * by embedding similarity can only be argued for; a choice point can be shown.
 *
 * `weight` is the MINIMUM of the two sides' reach, not the sum. A fork with a
 * hundred consequences one way and one the other is not really a choice — it is
 * a path with a footnote. A choice is where both answers cost something, so the
 * smaller branch is what makes it real.
 *
 * @param {Array<{id, parentId, valence}>} nodes
 * Nodes may carry isScenarioStarter / is_scenario_starter; variables and the
 * root are never choice points.
 * @returns {Array<{nodeId, wanted, unwanted, unknown, weight}>} strongest first
 */
export function detectChoicePoints(nodes = []) {
  const byId = new Map(nodes.map((n) => [n.id, n]));

  // How much hangs below each node, counted once.
  const reach = new Map();
  for (const n of nodes) {
    let cur = n.parentId ? byId.get(n.parentId) : null;
    let guard = 0;
    while (cur && guard < 40) {
      reach.set(cur.id, (reach.get(cur.id) || 0) + 1);
      cur = cur.parentId ? byId.get(cur.parentId) : null;
      guard += 1;
    }
  }
  const sizeOf = (id) => (reach.get(id) || 0) + 1; // the child itself counts

  const children = new Map();
  for (const n of nodes) {
    if (!n.parentId) continue;
    if (!children.has(n.parentId)) children.set(n.parentId, []);
    children.get(n.parentId).push(n);
  }

  const out = [];
  for (const [parentId, kids] of children) {
    // A variable's fan of consequences is an enumeration, not a fork — the room
    // listed what follows, it did not choose between them. The same goes for the
    // scenario root. A choice is a fork below that, where a path could go the
    // wanted way or the unwanted way.
    const parent = byId.get(parentId);
    if (!parent || !parent.parentId || parent.isScenarioStarter || parent.is_scenario_starter) continue;
    const wanted = kids.filter((k) => k.valence === 'wanted');
    const unwanted = kids.filter((k) => k.valence === 'unwanted');
    if (!wanted.length || !unwanted.length) continue;

    const side = (group) => group.reduce((sum, k) => sum + sizeOf(k.id), 0);
    out.push({
      nodeId: parentId,
      wanted: wanted.map((k) => k.id),
      unwanted: unwanted.map((k) => k.id),
      unknown: kids.filter((k) => k.valence === 'unknown' || !k.valence).map((k) => k.id),
      weight: Math.min(side(wanted), side(unwanted)),
    });
  }
  return out.sort((a, b) => b.weight - a.weight);
}
